import Vue from 'vue';
import VueApollo from 'vue-apollo';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { split, ApolloLink } from 'apollo-link';
import { WebSocketLink } from 'apollo-link-ws';
import { getMainDefinition } from 'apollo-utilities';
import { createUploadLink } from 'apollo-upload-client';
import { onError } from 'apollo-link-error';
import introspectionQueryResultData from '@/fragmentTypes.json';
import store from '@/store';
import errorHandler from '@/utils/error.handler';

Vue.use(VueApollo);

const AUTH_TOKEN = 'hey-token';
const httpEndpoint = process.env.VUE_APP_GRAPHQL_HTTP || 'http://localhost:4040/api/v1/graphql';
const wsEndpoint = process.env.VUE_APP_GRAPHQL_WS || 'ws://localhost:4040/subscriptions';
const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

// Call this in the Vue app file
export function createProvider() {
  // HTTP connection to the API
  const httpLink = createUploadLink({
    // You should use an absolute URL here
    uri: httpEndpoint,
  });

  const middlewareLink = new ApolloLink((operation, forward) => {
    const token = localStorage.getItem(AUTH_TOKEN);
    operation.setContext({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return forward(operation);
  });

  const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
    const { response } = operation.getContext();
    if (response && response.status === 401) {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(AUTH_TOKEN);
      }
      window.location.href = `${process.env.VUE_APP_LOGIN_BASE_URL}/logout`;
    }
    if (graphQLErrors) {
      graphQLErrors.map(({ message, locations, path }) => errorHandler.logErrors(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ));
    }
    if (networkError) {
      if (networkError.message.includes('Invalid Token')) {
        localStorage.removeItem(AUTH_TOKEN);
        window.location.href = `${process.env.VUE_APP_LOGIN_BASE_URL}/logout`;
      } else {
        errorHandler.logErrors(`[Network error]: ${networkError}`);
      }
    }
  });

  // Cache implementation
  const cache = new InMemoryCache({ fragmentMatcher });
  const wsLink = new WebSocketLink({
    uri: wsEndpoint,
    options: {
      reconnect: true,
      connectionParams: () => {
        const token = localStorage.getItem(AUTH_TOKEN);
        return { token };
      },
      inactivityTimeout: 1000,
    },
  });

  let timeOutAlert;
  wsLink.subscriptionClient.on('connected', () => {
    store.commit('itsReady', true);
  });

  wsLink.subscriptionClient.on('reconnected', () => {
    if (timeOutAlert) {
      clearTimeout(timeOutAlert);
      timeOutAlert = null;
    }
    store.commit('setAlertSocket', false);
  });

  wsLink.subscriptionClient.on('disconnected', () => {
    timeOutAlert = setTimeout(() => store.commit('setAlertSocket', true), 1500);
  });

  wsLink.subscriptionClient.maxConnectTimeGenerator.duration = () => (
    wsLink.subscriptionClient.maxConnectTimeGenerator.max
  );

  const link = split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    middlewareLink.concat(httpLink),
  );
  // Create the apollo client
  const apolloClient = new ApolloClient({
    link: ApolloLink.from([errorLink, link]),
    cache,
    connectToDevTools: true,
  });

  // Create vue apollo provider
  return new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: {
      $query: {
        fetchPolicy: 'cache-and-network',
      },
    },
    errorHandler(error) {
      // eslint-disable-next-line no-console
      console.log('%cError', 'background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;', error.message);
      errorHandler.logErrors(error.message);
    },
  });
}

// Manually call this when user log in
export async function onLogin(apolloClient, token) {
  if (typeof localStorage !== 'undefined' && token) {
    localStorage.setItem(AUTH_TOKEN, token);
  }

  try {
    await apolloClient.resetStore();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('%cError on cache reset (login)', 'color: orange;', e.message);
    errorHandler.logErrors(e.message);
  }
}

// Manually call this when user log out
export async function onLogout(apolloClient) {
  try {
    window.localStorage.clear();
    await apolloClient.resetStore();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('%cError on cache reset (logout)', 'color: orange;', e.message);
    errorHandler.logErrors(e.message);
  } finally {
    window.location.href = `${process.env.VUE_APP_LOGIN_BASE_URL}/logout`;
  }
}

// Get Token
export function getToken(tokenName = AUTH_TOKEN) {
  return localStorage.getItem(tokenName);
}
